import React, { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { requestAPI } from '../../data/api'
import RejectViolationModal from './RejectViolationModal'
import './ViolationPage.css'

const REVIEW_EVENT_LABELS = {
	ApprovedCaseByCase: 'Approved (case by case)',
	ApprovedMoreCases: 'Approved (more cases)',
	ApprovedBulk: 'Approved (bulk)',
	PutOnHold: 'Put on hold',
	PutOffHold: 'Put off hold',
	MarkAsSoftClaim: 'Marked as soft claim',
	UnmarkAsSoftClaim: 'Unmarked as soft claim',
	MarkAsCommercial: 'Marked as commercial',
	MarkAsEditorial: 'Marked as editorial',
	Whitelisted: 'Rejected — whitelisted',
	SingleSale: 'Rejected — single sale',
	OtherReason: 'Rejected — other reason',
	Handout: 'Rejected — handout',
	UnknownPhotographer: 'Rejected — unknown photographer',
	UnknownDate: 'Rejected — unknown date',
	OutOfCopyright: 'Rejected — out of copyright',
	CounterpartyOnPicture: 'Rejected — counterparty on picture',
	OtherUnclearReason: 'Rejected — other unclear reason',
}

const formatDate = (timestamp) => {
	if (!timestamp) return '—'
	const date = new Date(timestamp)
	if (Number.isNaN(date.getTime())) return '—'
	return date.toLocaleString('en-GB', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

const userName = (entry) => {
	if (entry.user_firstname || entry.user_lastname) {
		return `${entry.user_firstname || ''} ${entry.user_lastname || ''}`.trim()
	}
	return entry.user_id ? entry.user_id.slice(0, 8) : 'system'
}

const Field = ({ label, value }) => (
	<div className='vp-field'>
		<span className='vp-field-label'>{label}</span>
		<span className='vp-field-value'>{value || '—'}</span>
	</div>
)

const StatePill = ({ state }) => (
	<span className={`vp-state-pill vp-state-${state || 'unknown'}`}>
		{state || 'unknown'}
	</span>
)

const TimelineEntry = ({ entry }) => {
	const renderPayload = () => {
		switch (entry.kind) {
			case 'review_event': {
				const label = REVIEW_EVENT_LABELS[entry.payload.event_type] || entry.payload.event_type
				const comment = entry.payload.context && entry.payload.context.comment
				return (
					<>
						<div className='vp-tl-title'>{label}</div>
						{comment && <div className='vp-tl-detail'>"{comment}"</div>}
					</>
				)
			}
			case 'contact_enriched': {
				const p = entry.payload
				const emails = Array.isArray(p.email_addresses) ? p.email_addresses : []
				const phones = Array.isArray(p.phone_numbers) ? p.phone_numbers : []
				return (
					<>
						<div className='vp-tl-title'>Contact info enriched</div>
						<div className='vp-tl-detail'>
							{p.company_name || '(no company name)'}
							{p.company_number ? ` · ${p.company_number}` : ''}
							{p.country ? ` · ${p.country}` : ''}
						</div>
						{(emails.length > 0 || phones.length > 0) && (
							<div className='vp-tl-detail'>
								{emails.length > 0 && <>Email: {emails.join(', ')} </>}
								{phones.length > 0 && <>Phone: {phones.join(', ')}</>}
							</div>
						)}
					</>
				)
			}
			case 'case_prepared': {
				const p = entry.payload
				return (
					<>
						<div className='vp-tl-title'>
							Case prep: {p.outcome}
							{p.filter_reason ? ` (${p.filter_reason})` : ''}
						</div>
						<div className='vp-tl-detail'>
							{p.site_type ? `${p.site_type} · ` : ''}
							{p.is_commercial != null ? (p.is_commercial ? 'commercial' : 'editorial') : ''}
							{p.contact_found != null ? ` · contact ${p.contact_sufficient ? 'sufficient' : p.contact_found ? 'found' : 'not found'}` : ''}
						</div>
					</>
				)
			}
			case 'case_decided':
				return (
					<div className='vp-tl-title'>
						Case {entry.payload.decision}
					</div>
				)
			default:
				return <div className='vp-tl-title'>{entry.kind}</div>
		}
	}

	return (
		<div className={`vp-tl-entry vp-tl-${entry.kind}`}>
			<div className='vp-tl-meta'>
				<span className='vp-tl-time'>{formatDate(entry.occurred_at)}</span>
				<span className='vp-tl-user'>{userName(entry)}</span>
			</div>
			<div className='vp-tl-body'>{renderPayload()}</div>
		</div>
	)
}

const ViolationPage = ({ match }) => {
	const { violationId } = match.params
	const [violation, setViolation] = useState(null)
	const [timeline, setTimeline] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [rejectOpen, setRejectOpen] = useState(false)

	const fetchAll = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const [v, t] = await Promise.all([
				requestAPI(`/violations/${violationId}/details`, 'GET'),
				requestAPI(`/violations/${violationId}/timeline`, 'GET'),
			])
			if (v && v.message === 'Violation not found') {
				setError('Violation not found')
			} else {
				setViolation(v)
				setTimeline(Array.isArray(t) ? t : [])
			}
		} catch (e) {
			setError('Failed to load violation')
		} finally {
			setLoading(false)
		}
	}, [violationId])

	useEffect(() => {
		fetchAll()
	}, [fetchAll])

	if (loading) {
		return (
			<div className='vp-container'>
				<div className='vp-loading'>Loading…</div>
			</div>
		)
	}

	if (error || !violation) {
		return (
			<div className='vp-container'>
				<div className='vp-error'>{error || 'Violation not found'}</div>
			</div>
		)
	}

	const isRejected = violation.state === 'rejected'

	return (
		<div className='vp-container'>
			<div className='vp-header'>
				<div>
					<h2 className='vp-title'>
						Violation <span className='vp-mono'>{violation.violation_id}</span>
					</h2>
					<div className='vp-subtitle'>
						<StatePill state={violation.state} />
						{violation.case_id && (
							<span className='vp-case-id'>case · <span className='vp-mono'>{violation.case_id}</span></span>
						)}
						{violation.domain && (
							<a
								href={`https://${violation.domain}`}
								target='_blank'
								rel='noopener noreferrer'
								className='vp-domain-link'
							>
								{violation.domain}
							</a>
						)}
					</div>
				</div>
				<div className='vp-actions'>
					{/*TODO add reject button back*/}
					{/*<button*/}
					{/*	className='vp-reject-button'*/}
					{/*	onClick={() => setRejectOpen(true)}*/}
					{/*	disabled={isRejected}*/}
					{/*	title={isRejected ? 'Already rejected' : 'Reject this case'}*/}
					{/*>*/}
					{/*	{isRejected ? 'Rejected' : 'Reject case'}*/}
					{/*</button>*/}
				</div>
			</div>

			<div className='vp-grid'>
				<section className='vp-card'>
					<h3 className='vp-card-title'>Status</h3>
					<Field label='State' value={violation.state} />
					<Field label='Market' value={violation.market} />
					<Field label='Violation type' value={violation.violation_type} />
					<Field
						label='Is violating'
						value={violation.is_violating ? 'Yes' : 'No'}
					/>
					<Field
						label='Non-violating reason'
						value={violation.non_violating_reason}
					/>
					<Field label='Soft claim' value={violation.soft_claim ? 'Yes' : 'No'} />
					<Field label='Manipulated' value={violation.manipulated ? 'Yes' : 'No'} />
					<Field
						label='Customer approve time'
						value={formatDate(violation.customer_approve_time)}
					/>
					<Field label='Created' value={formatDate(violation.reg_date)} />
					<Field label='Last modified' value={formatDate(violation.last_modified)} />
				</section>

				<section className='vp-card'>
					<h3 className='vp-card-title'>Counterparty</h3>
					<Field label='Organization' value={violation.organization_name} />
					<Field label='Company number' value={violation.company_number} />
					<Field label='Type' value={violation.counterparty_type} />
					<Field label='Address' value={violation.address} />
					<Field
						label='Postal code · city'
						value={
							[violation.postal_code, violation.city].filter(Boolean).join(' · ') || null
						}
					/>
					<Field label='Country' value={violation.country} />
					<Field
						label='Email addresses'
						value={
							violation.email_addresses.length > 0
								? violation.email_addresses.join(', ')
								: null
						}
					/>
					<Field
						label='Phone numbers'
						value={
							violation.phone_numbers.length > 0
								? violation.phone_numbers.join(', ')
								: null
						}
					/>
				</section>

				<section className='vp-card'>
					<h3 className='vp-card-title'>Source</h3>
					<Field
						label='URL'
						value={
							violation.url_raw ? (
								<a href={violation.url_raw} target='_blank' rel='noopener noreferrer'>
									{violation.url_raw}
								</a>
							) : null
						}
					/>
					<Field label='Title' value={violation.title} />
					<Field
						label='First date of publication'
						value={formatDate(violation.first_date_of_publication)}
					/>
					<Field
						label='Wayback URL'
						value={
							violation.wayback_url ? (
								<a href={violation.wayback_url} target='_blank' rel='noopener noreferrer'>
									{violation.wayback_url}
								</a>
							) : null
						}
					/>
					<Field label='Comment' value={violation.comment} />
					<Field label='Internal comment' value={violation.internal_comment} />
				</section>
			</div>

			<section className='vp-timeline'>
				<h3 className='vp-card-title'>Timeline</h3>
				{timeline.length === 0 ? (
					<div className='vp-tl-empty'>No timeline events recorded.</div>
				) : (
					<div className='vp-tl-list'>
						{timeline.map((entry) => (
							<TimelineEntry key={entry.id} entry={entry} />
						))}
					</div>
				)}
			</section>

			<div className='vp-back'>
				<Link to='/statistics?tab=violation-overview'>← Back to Violation overview</Link>
			</div>

			{rejectOpen && (
				<RejectViolationModal
					violationId={violation.violation_id}
					clientId={violation.client_id}
					onClose={() => setRejectOpen(false)}
					onRejected={() => {
						setRejectOpen(false)
						fetchAll()
					}}
				/>
			)}
		</div>
	)
}

export default ViolationPage
